import React, { useEffect, useMemo } from "react";
import ReactJson from "react-json-view";
import { useImmer } from "use-immer";

import {
  ColorError,
  ColorWarning,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { GetMetafieldRequestQueryParamType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useGetMetafield } from "../../../api/shops/get-metafield";
import { SkeletonGroup } from "../../../components/Utils/SkeletonGroup";

interface MetafieldLookupSectionProps {
  metafieldKey: string;
  type: "product" | "variant";
}

export const MetafieldLookupSection = ({
  metafieldKey,
  type,
}: MetafieldLookupSectionProps) => {
  const [params, setParams] = useImmer<
    Partial<GetMetafieldRequestQueryParamType>
  >({});
  const [id, setId] = useImmer<string>("");

  const metafield = useGetMetafield(params);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const timer = setTimeout(async () => {
      setParams({ id, key: metafieldKey, type });
    }, 250);
    return () => clearTimeout(timer);
  }, [id]);

  const message = useMemo(() => {
    if (metafield.isLoading) {
      return <SkeletonGroup lines={1} />;
    }
    if (metafield.isSuccess) {
      if (metafield.data.metafield?.id) {
        if (metafield.data.metafield.metafield?.value) {
          return (
            <ReactJson
              name={"igTestGroups"}
              theme={"shapeshifter:inverted"}
              shouldCollapse={false}
              src={JSON.parse(metafield.data.metafield.metafield.value)}
              sortKeys={true}
            />
          );
        }
        return (
          <IgTypography sx={{ color: ColorError.main, fontWeight: 600 }}>
            No metafield found
          </IgTypography>
        );
      }
      return (
        <IgTypography sx={{ color: ColorWarning.dark, fontWeight: 600 }}>
          Invalid Variant id
        </IgTypography>
      );
    }
    return (
      <IgTypography>Enter a Variant ID to check its metafield</IgTypography>
    );
  }, [metafield.isSuccess, metafield.isLoading, metafield.data]);

  return (
    <Grid
      container
      direction={"column"}
      item
      sx={{ width: "100%" }}
      rowSpacing={1}
    >
      <Grid item>
        <IgTypography variant="h6">Check Variant Metafield</IgTypography>
      </Grid>
      <Grid item>
        <IgTextField
          label={`Variant ID`}
          value={id}
          onChange={(e) => {
            setId(e.target.value);
          }}
        />
      </Grid>
      <Grid item>{message}</Grid>
    </Grid>
  );
};
